import { getMatchingName, getPricePerInstallment, getQueryFilter } from '@skytech/utils'
import minBy from 'lodash/minBy'

import type { Product, ProductGroupData } from '@/components/ecom-policy/types'

export const handleMultipleChoiceItemSelect = ({
  selectedId,
  currentIdList,
  setData,
}: {
  selectedId: number
  currentIdList: number[] | null
  setData: (idList: number[]) => void
}) => {
  if (currentIdList?.includes(selectedId)) {
    setData(currentIdList.filter(currentIdListItem => currentIdListItem !== selectedId))
  } else {
    setData([...(currentIdList || []), selectedId])
  }
}

export const parseInStock = (inStock: string | null) => {
  if (inStock === null) return '0'

  const inStockWithoutSigns = inStock?.replace(/\+|</, '') || 0
  const inStockNumber = Number(inStockWithoutSigns)

  return inStockNumber > 0 ? inStock : '0'
}

export const getNumberOfProductsInStock = (productsList: Product[]): string => {
  const numberOfProductsInStock = productsList.reduce((acc, { inStock }) => {
    const parsedInStock = inStock?.replace(/\+|</, '') || 0

    // disabling due to reduce func
    // eslint-disable-next-line no-param-reassign
    acc += Number(parsedInStock)
    return acc
  }, 0)

  return numberOfProductsInStock > 0 ? `${numberOfProductsInStock}+` : '0'
}

const inventoryStringMatcher = (listItem: Product) => [listItem.name, listItem.alias]

export const getGroupedDataList = ({
  priceList,
  groupNames,
  selectedProductIds,
  selectedAccessoriesIds,
  searchQuery,

  isRelationsIgnored,
}: {
  selectedProductIds: number[] | null
  selectedAccessoriesIds: number[] | null
  priceList?: Product[]
  groupNames?: string[]
  searchQuery?: string
  isRelationsIgnored: boolean
}) => {
  const list = (priceList || []).reduce(
    (acc, product) => {
      const {
        id,
        category,
        price,
        name: productName,
        imgUrl: imageUrl,
        assetType,
        description,
        parentProductIdList,
        alias: productAlias,
        aliasDescription: productAliasDescription,
        inStock,
        isAccessory: isAccessoryRaw,
        isMandatory: isMandatoryRaw,
      } = product

      // Transportation cost - to be added in the last step automatically
      if (category === 'SHIPPING') return acc

      const isAccessory = Boolean(isAccessoryRaw)
      const isMandatory = Boolean(isMandatoryRaw)

      if (isRelationsIgnored && isMandatory) return acc

      // A product with the isAccessory attr should be visible as an accessory if it has no parent products
      const isParentProduct = (!parentProductIdList && !isAccessory) || isRelationsIgnored
      const parentProductIds = parentProductIdList?.split(',')
      const isAccessoryAvailable = Boolean(isAccessory || parentProductIdList)
      const isParentProductSelected = Boolean(
        selectedProductIds?.find(id => parentProductIds?.includes(String(id)))
      )

      const matchingName = getMatchingName({
        groupNames,
        productName,
      })

      // Hack for showing accessories in the edit-mode
      const isProductAccessoriesVisible =
        isAccessoryAvailable || (selectedProductIds?.includes(id) && !isParentProduct)

      if (!matchingName) {
        // Filters
        const isItemVisible = Boolean(
          [product].find(getQueryFilter(searchQuery, inventoryStringMatcher))
        )

        if (!isItemVisible) return acc
        const groupKey = productName + ' - ' + product.category
        if (isParentProduct) {
          acc.groupedProducts[id] = {
            description: productAliasDescription || description,
            category,
            imageUrl,
            assetType,
            groupKey,
            groupedProducts: [product],
            groupName: productAlias || productName,
            priceFrom: price,
            isAccessory,
            inStock: parseInStock(inStock),
            isMandatory,
          }
        }

        if (isRelationsIgnored) return acc

        if (isProductAccessoriesVisible) {
          acc.groupedAccessories[id] = {
            description: productAliasDescription || description,
            category,
            imageUrl,
            assetType,
            groupKey,
            groupedProducts: [product],
            groupName: productAlias || productName,
            priceFrom: price,
            inStock: parseInStock(inStock),
            isAccessory,
            isRecommended: !isMandatory && isParentProductSelected,
            isMandatory,
          }

          if (!parentProductIdList || (parentProductIdList && isParentProductSelected)) {
            acc.selectableAccessoryIds.push(id)
          }
        }

        return acc
      }

      const groupKey = matchingName + ' - ' + product.category
      const currentGroupedProducts = isParentProduct ? acc.groupedProducts : acc.groupedAccessories
      const updatedGroupedProducts = [
        ...(currentGroupedProducts[groupKey]?.groupedProducts || []),
        product,
      ].filter(getQueryFilter(searchQuery, inventoryStringMatcher))
      const isWholeGroupMandatory = updatedGroupedProducts.every(({ isMandatory }) => isMandatory)

      // Filters
      const isGroupVisible = updatedGroupedProducts.length > 0

      if (!isGroupVisible) return acc

      if (isParentProduct) {
        const priceFrom = minBy(updatedGroupedProducts, product => product.price)?.price || 0
        const selectedProduct = updatedGroupedProducts.find(({ id }) =>
          selectedProductIds?.includes(id)
        )
        const numberOfProductsInStock = getNumberOfProductsInStock(
          selectedProduct ? [selectedProduct] : updatedGroupedProducts
        )

        acc.groupedProducts[groupKey] = {
          groupKey,
          priceFrom,
          category,
          groupedProducts: updatedGroupedProducts,
          assetType,
          groupName:
            updatedGroupedProducts.length === 1 ? productAlias || productName : matchingName,
          description:
            updatedGroupedProducts.length === 1
              ? productAliasDescription || description
              : description,
          imageUrl,
          isAccessory,
          inStock: numberOfProductsInStock,
          isMandatory: isWholeGroupMandatory,
        }
      }

      if (isRelationsIgnored) return acc

      if (isProductAccessoriesVisible) {
        const selectedProducts = updatedGroupedProducts.filter(({ id }) =>
          selectedAccessoriesIds?.includes(id)
        )
        const isGroupSelected = selectedProducts.length > 0
        const priceFrom =
          minBy(
            isGroupSelected ? selectedProducts : updatedGroupedProducts,
            product => product.price
          )?.price || 0
        const numberOfProductsInStock = getNumberOfProductsInStock(
          selectedProducts.length > 0 ? selectedProducts : updatedGroupedProducts
        )

        acc.groupedAccessories[groupKey] = {
          groupKey,
          priceFrom,
          category,
          groupedProducts: updatedGroupedProducts,
          assetType,
          groupName:
            updatedGroupedProducts.length === 1 ? productAlias || productName : matchingName,
          description:
            updatedGroupedProducts.length === 1
              ? productAliasDescription || description
              : description,
          imageUrl,
          inStock: numberOfProductsInStock,
          isAccessory,
          // At least one product with a selected parent
          isRecommended:
            (acc.groupedAccessories[groupKey]?.isRecommended || isParentProductSelected) &&
            !isWholeGroupMandatory,
          isMandatory: isWholeGroupMandatory,
        }

        if (!parentProductIdList || (parentProductIdList && isParentProductSelected)) {
          acc.selectableAccessoryIds.push(id)
        }
      }

      return acc
    },
    {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      groupedProducts: {} as Record<string, ProductGroupData>,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      groupedAccessories: {} as Record<string, ProductGroupData>,
      selectableAccessoryIds: [] as number[],
    }
  )

  return list
}

export const getDifferenceInMonths = ({
  startDate,
  endDate,
}: {
  startDate: Date
  endDate: Date
}): number => {
  let months =
    (endDate.getFullYear() - startDate.getFullYear()) * 12 +
    (endDate.getMonth() - startDate.getMonth())
  if (endDate.getDate() < startDate.getDate()) {
    months -= 1
  }
  return Math.max(0, months)
}

export const recalculatePriceWithCover = ({
  coverAvailable,
  priceList,
  selectedProductIds,
}: {
  coverAvailable: number
  priceList: Product[]
  selectedProductIds: number[]
}) => {
  let coverLeft = Math.max(0, coverAvailable)
  const allocation: Record<number, { covered: number; userPrice: number }> = {}

  // Build list of selected products in order of selection
  selectedProductIds.forEach(productId => {
    if (!allocation[productId]) {
      allocation[productId] = { covered: 0, userPrice: 0 }
    }
    const product = priceList.find(p => p.id === productId)
    if (!product) return
    const covered = Math.min(product.price, coverLeft)
    const userPrice = product.price - covered
    allocation[productId] = { covered, userPrice }
    coverLeft -= covered
  })

  return {
    allocation,
    coverLeft,
  }
}

export const getPriceDetails = (price: number, coverInterval: number, rowRate: number) => {
  if (price === 0) {
    return { monthlyPrice: 0, taxSavings: 0, realPriceAfterTax: 0 }
  }
  const monthlyPrice = getPricePerInstallment(price, coverInterval)

  const taxSavings = monthlyPrice * (rowRate / 100)
  const realPriceAfterTax = Math.max(0, monthlyPrice - taxSavings)

  return { monthlyPrice, taxSavings, realPriceAfterTax }
}
